
import LotteryActivityPrizes from "@/components/xcoins/lottery/LotteryActivityPrizes.vue";
import LotteryAddressDialog from "@/components/xcoins/lottery/LotteryAddressDialog.vue";
import LotteryHeroWheel from "@/components/xcoins/lottery/LotteryHeroWheel.vue";
import LotteryMyPrizes from "@/components/xcoins/lottery/LotteryMyPrizes.vue";
import LotteryRules from "@/components/xcoins/lottery/LotteryRules.vue";
import LotteryWinningRecords from "@/components/xcoins/lottery/LotteryWinningRecords.vue";
import {
  DEFAULT_WHEEL_PRIZES,
  appendImageResize,
  getPrizeName,
  isApiSuccess,
} from "@/components/xcoins/lottery/lotteryUtils";

export default {
  name: "xcoinsLottery",
  components: {
    LotteryActivityPrizes,
    LotteryAddressDialog,
    LotteryHeroWheel,
    LotteryMyPrizes,
    LotteryRules,
    LotteryWinningRecords,
  },
  layout: "DefaultLayout",
  head() {
    return {
      title: this.activity.pageTitle || this.activity.name || "Lucky Wheel",
    };
  },
  async asyncData({ $global, query, redirect, store }) {
    let activityId = query.id;

    try {
      if (!activityId) {
        const listRes = await $global.pointsLotteryActivityList().catch(() => null);
        const list = isApiSuccess(listRes) && Array.isArray(listRes.data) ? listRes.data : [];
        if (!list.length) {
          redirect("/xcoins/exchange");
          return;
        }
        activityId = list[0]?.id || list[0]?.activityId;
      }

      if (!activityId) {
        redirect("/xcoins/exchange");
        return;
      }

      // 客户端已登录时，详情与用户数据统一在 mounted 中只请求一次（带 token）
      if (process.client && store?.state?.isLogin) {
        return {
          activity: {},
          currentActivityId: activityId,
        };
      }

      const detailRes = await $global.pointsLotteryActivityDetail(activityId).catch(() => null);
      if (!isApiSuccess(detailRes)) {
        redirect("/xcoins/exchange");
        return;
      }

      return {
        activity: detailRes.data || {},
        currentActivityId: activityId,
      };
    } catch (e) {
      redirect("/xcoins/exchange");
    }
  },
  data() {
    return {
      activity: {},
      currentActivityId: "",
      pointToken: "",
      pageDataBootstrapped: false,
      activityLoadedWithToken: false,
      loading: false,
      myPrizeLoading: false,
      pendingUserLotteryRequest: null,
      isDrawing: false,
      isMobile: false,
      myPrizeList: [],
      myPrizeTotal: 0,
      myPrizeCurrentPage: 1,
      myPrizePageSize: 9,
      resultDialogVisible: false,
      drawResult: {},
      addressBookVisible: false,
      needsMyPrizeRefresh: false,
    };
  },
  computed: {
    defaultCopy() {
      return {
        drawNow: this.$t("xcoins.DRAW_NOW"),
        drawing: this.$t("xcoins.DRAWING"),
        myPoints: this.$t("xcoins.DRAW_MY_XCOINS"),
        todayDrawn: this.$t("xcoins.TODAYS_DRAWS"),
        recordsTitle: this.$t("xcoins.WINNING_RECORDS"),
        activityPrizesTitle: this.$t("xcoins.WIN_PRIZE_NAME"),
        myPrizesTitle: this.$t("xcoins.MY_PRIZES"),
        prizeTime: this.$t("xcoins.WIN_TIME"),
        prizeName: this.$t("xcoins.PRIZE_NAME"),
        prizeDetail: this.$t("xcoins.PRIZE_DETAILS"),
        rulesTitle: this.$t("xcoins.EVENT_RULES"),
        congratulations: this.$t("xcoins.CONGRATULATIONS"),
        win: this.$t("xcoins.GOT_PRIZE"),
        in: this.$t("xcoins.DRAW_IN"),
        noRecords: this.$t("xcoins.NO_WIN_RECORDS"),
        noPrizes: this.$t("xcoins.NO_PRIZES_TIP"),
        joinDraw: this.$t("xcoins.ENTER_DRAW"),
        endedTitle: this.$t("xcoins.DRAW_ENDED"),
        endedTip: this.$t("xcoins.DRAW_ENDED_NOTICE"),
        insufficient: this.$t("xcoins.INSUFFICIENT_XCOINS"),
        loginRequired: this.$t("xcoins.LOGIN_TO_DRAW"),
        noChance: this.$t("xcoins.NO_DRAWS_LEFT"),
        drawFailed: this.$t("xcoins.DRAW_FAILED"),
        almost: this.$t("xcoins.SO_CLOSE"),
        tryAgain: this.$t("xcoins.KEEP_TRYING"),
        confirm: this.$t("xcoins.CONFIRM"),
        fillAddress: this.$t("xcoins.FILL_SHIPPING_ADDR"),
        copy: this.$t("xcoins.COPY_ADDRESS"),
        prizeExhaustedToday: this.$t("xcoins.PRIZES_ALL_DISTRIBUTED"),
      };
    },
    isEnded() {
      return Number(this.activity.status) === 4;
    },
    isPrizeExhaustedToday() {
      return this.activity.prizeExhaustedToday === true;
    },
    myPrizePaginationLayout() {
      return this.isMobile
        ? "total, prev, pager, next"
        : "total, prev, pager, next, jumper";
    },
    wheelPrizes() {
      const list = Array.isArray(this.activity.prizeList) ? this.activity.prizeList.filter(Boolean) : [];
      return list.length ? list : DEFAULT_WHEEL_PRIZES;
    },
    lotteryPageStyle() {
      const image = this.isMobile
        ? (this.activity.mobileBgImageUrl || this.activity.bgImageUrl)
        : this.activity.bgImageUrl;
      if (!image) {
        return {};
      }
      return {
        backgroundImage: `url('${image}')`,
      };
    },
    displayUserPoints() {
      const value = this.$store.state.isLogin ? this.activity.userPoints : 0;
      return this.formatPoints(value);
    },
    costText() {
      return this.$t("xcoins.SPEND_XCOINS_DRAW", {
        point: this.formatPoints(this.activity.singleCostPoints || 0),
      });
    },
    dailyLimitText() {
      return this.$t("xcoins.DRAWS_LEFT_TODAY", {
        item: this.displayRemainToday,
      });
    },
    displayRemainToday() {
      if (this.$store.state.isLogin && this.activity.userRemainToday !== null && this.activity.userRemainToday !== undefined) {
        return Number(this.activity.userRemainToday) || 0;
      }
      return Number(this.activity.dailyLimitPerUser) || 0;
    },
    drawnTodayText() {
      const drawn = this.$store.state.isLogin ? Number(this.activity.userDrawnToday) || 0 : 0;
      return `${drawn}`;
    },
    recentWinners() {
      return Array.isArray(this.activity.recentWinners) ? this.activity.recentWinners : [];
    },
    resultPrizeType() {
      return Number(this.drawResult.prizeType || this.drawResult.type);
    },
    isNoPrizeResult() {
      return this.resultPrizeType === 5;
    },
    isPhysicalPrizeResult() {
      return this.resultPrizeType === 1;
    },
    resultDialogTitle() {
      return this.isNoPrizeResult ? this.defaultCopy.almost : this.defaultCopy.congratulations;
    },
    resultDialogDescription() {
      if (this.isNoPrizeResult) {
        return this.defaultCopy.tryAgain;
      }

      return `${this.defaultCopy.win} ${this.getPrizeName(this.drawResult)}`;
    },
    resultPrimaryButtonText() {
      return this.isPhysicalPrizeResult ? this.defaultCopy.fillAddress : this.defaultCopy.confirm;
    },
    resultVisualImage() {
      if (this.isNoPrizeResult) {
        return "";
      }

      return this.appendImageResize(this.drawResult.prizeImageUrl || this.drawResult.imageUrl || "");
    },
  },
  watch: {
    "$store.state.isLogin"(next, prev) {
      if (next && !prev) {
        this.pageDataBootstrapped = false;
        this.activityLoadedWithToken = false;
        this.bootstrapPageData();
        return;
      }

      if (!next) {
        this.resetMyPrizeList();
        this.pageDataBootstrapped = false;
        this.activityLoadedWithToken = false;
      }
    },
  },
  mounted() {
    this.updateViewport();
    window.addEventListener("resize", this.updateViewport);
    this.bootstrapPageData();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateViewport);
  },
  methods: {
    // 记录当前视口类型，驱动移动端分页和转盘指针方向。
    updateViewport() {
      this.isMobile = window.innerWidth <= 768;
    },
    // 清空我的奖品分页数据，常用于退出登录或接口失败兜底。
    resetMyPrizeList() {
      this.myPrizeList = [];
      this.myPrizeTotal = 0;
      this.myPrizeCurrentPage = 1;
    },
    // 首次进入页面时拉取登录态相关数据，避免重复请求。
    bootstrapPageData() {
      if (this.pageDataBootstrapped) {
        return;
      }

      this.pageDataBootstrapped = true;

      if (!this.$store.state.isLogin) {
        return;
      }

      return this.fetchUserLotteryData({ page: 1, updateDetail: true });
    },
    // 合并活动详情和我的奖品接口响应，保持页面状态同步。
    applyUserLotteryResponses(detailRes, prizeRes, page = 1) {
      if (detailRes) {
        if (isApiSuccess(detailRes)) {
          this.activity = detailRes.data || {};
          this.activityLoadedWithToken = true;
        } else if (!this.getActivityId()) {
          this.$router.replace({ path: "/xcoins/exchange" });
        }
      }

      if (!prizeRes) {
        return;
      }

      if (isApiSuccess(prizeRes)) {
        const data = prizeRes.data || {};
        const list = Array.isArray(data.list) ? data.list : [];
        this.myPrizeList = list;
        this.myPrizeTotal = Number(data.total) || list.length;
        this.myPrizeCurrentPage = Number(data.current) || page;
        this.myPrizePageSize = Number(data.size) || this.myPrizePageSize;
        return;
      }

      this.resetMyPrizeList();
    },
    // 并发拉取活动详情和我的奖品列表，登录用户统一携带积分 token。
    async fetchUserLotteryData({ page = 1, updateDetail = true, force = false } = {}) {
      if (!this.$store.state.isLogin) {
        return;
      }

      const activityId = this.getActivityId();
      if (!activityId) {
        this.$router.replace({ path: "/xcoins/exchange" });
        return;
      }

      if (!force && this.pendingUserLotteryRequest) {
        return this.pendingUserLotteryRequest;
      }

      if (updateDetail) {
        this.loading = true;
      }
      this.myPrizeLoading = true;

      this.pendingUserLotteryRequest = (async () => {
        try {
          const result = await this.$pointsCache.requestWithPointToken(async (token) => {
            this.pointToken = token;
            const requests = [
              updateDetail
                ? this.$global.pointsLotteryActivityDetail(activityId, token)
                : Promise.resolve(null),
              this.$global.pointsLotteryRecordPage(
                {
                  activityId,
                  limit: this.myPrizePageSize,
                  page,
                },
                token
              ),
            ];
            const [detailRes, prizeRes] = await Promise.all(requests);
            return { detailRes, prizeRes };
          });

          if (!result) {
            return;
          }

          this.applyUserLotteryResponses(result.detailRes, result.prizeRes, page);
        } catch (e) {
          if (updateDetail && !this.getActivityId()) {
            this.$router.replace({ path: "/xcoins/exchange" });
          }
          if (!force) {
            this.resetMyPrizeList();
          }
        } finally {
          if (updateDetail) {
            this.loading = false;
          }
          this.myPrizeLoading = false;
        }
      })();

      try {
        return await this.pendingUserLotteryRequest;
      } finally {
        this.pendingUserLotteryRequest = null;
      }
    },
    // 我的奖品分页切换时重新请求对应页数据。
    handleMyPrizePageChange(page) {
      this.fetchMyPrizeList(page);
    },
    // 单独刷新我的奖品列表，地址提交或补刷时会强制更新 token 数据。
    async fetchMyPrizeList(page = this.myPrizeCurrentPage, forceToken = false) {
      if (!this.$store.state.isLogin || (this.myPrizeLoading && !forceToken)) {
        return;
      }

      if (page === this.myPrizeCurrentPage && !forceToken && this.myPrizeList.length > 0) {
        return;
      }

      this.myPrizeLoading = true;
      try {
        const res = await this.$pointsCache.requestWithPointToken((token) => {
          this.pointToken = token;
          return this.$global.pointsLotteryRecordPage(
            {
              activityId: this.getActivityId(),
              limit: this.myPrizePageSize,
              page,
            },
            token
          );
        });

        if (isApiSuccess(res)) {
          const data = res.data || {};
          const list = Array.isArray(data.list) ? data.list : [];
          this.myPrizeList = list;
          this.myPrizeTotal = Number(data.total) || list.length;
          this.myPrizeCurrentPage = Number(data.current) || page;
          this.myPrizePageSize = Number(data.size) || this.myPrizePageSize;
          return;
        }

        if (forceToken) {
          this.resetMyPrizeList();
        }
      } catch (e) {
        if (forceToken) {
          this.resetMyPrizeList();
        }
      } finally {
        this.myPrizeLoading = false;
      }
    },
    // 未登录抽奖时打开当前站点配置对应的登录入口。
    openLoginWithSource() {
      const useNewLogin = this.$store.state.siteConfig && this.$store.state.siteConfig.DialogPassportSwitch == "1";
      if (useNewLogin) {
        this.$store.commit("OPEN_AUTH_MODAL", {
          tab: "login",
          redirectToUserCenter: false,
          source: {
            elementTypeName: "积分抽奖",
            clickName: "lottery draw",
            loginChannel: "积分抽奖",
          },
        });
      } else {
        this.$jumpTerminus(1);
      }
    },
    // 抽奖结束后刷新活动余额、剩余次数和我的奖品列表。
    async refreshAfterDraw() {
      this.activityLoadedWithToken = false;
      this.myPrizeCurrentPage = 1;
      this.needsMyPrizeRefresh = true;
      try {
        await this.fetchUserLotteryData({ page: 1, updateDetail: true, force: true });
      } finally {
        this.needsMyPrizeRefresh = false;
      }
    },
    // 兼容路由和接口返回里的不同活动 id 字段。
    getActivityId() {
      return this.activity.activityId || this.currentActivityId || this.$route.query.id || "";
    },
    // 单独获取活动详情，保留给需要只刷新详情的流程使用。
    async fetchActivityDetail(forceToken = false) {
      if (this.loading) {
        return;
      }

      if (forceToken && this.activityLoadedWithToken) {
        return;
      }

      const id = this.currentActivityId || this.$route.query.id;
      if (!id) {
        if (!this.getActivityId()) {
          this.$router.replace({ path: "/xcoins/exchange" });
        }
        return;
      }

      this.loading = true;
      try {
        let res;

        if (this.$store.state.isLogin && forceToken) {
          res = await this.$pointsCache.requestWithPointToken((token) => {
            this.pointToken = token;
            return this.$global.pointsLotteryActivityDetail(id, token);
          });
        } else {
          res = await this.$global.pointsLotteryActivityDetail(id).catch(() => null);
        }

        if (isApiSuccess(res)) {
          this.activity = res.data || {};
          if (forceToken) {
            this.activityLoadedWithToken = true;
          }
        } else if (!this.getActivityId()) {
          this.$router.replace({ path: "/xcoins/exchange" });
        }
      } catch (e) {
        if (!this.getActivityId()) {
          this.$router.replace({ path: "/xcoins/exchange" });
        }
      } finally {
        this.loading = false;
      }
    },
    // 执行抽奖主流程：校验登录和积分，调用接口，触发转盘动画与结果弹窗。
    async drawLottery() {
      if (this.isDrawing || this.loading || this.isEnded || this.isPrizeExhaustedToday) {
        return;
      }

      if (!this.$store.state.isLogin) {
        this.openLoginWithSource();
        return;
      }

      const cost = this.parsePointValue(this.activity.singleCostPoints);
      const points = this.parsePointValue(this.activity.userPoints);
      if (cost > 0 && points < cost) {
        this.$message.warning(this.defaultCopy.insufficient);
        return;
      }

      if (this.displayRemainToday <= 0) {
        this.$message.warning(this.defaultCopy.noChance);
        return;
      }

      const activityId = this.activity.activityId || this.currentActivityId || this.$route.query.id;
      if (!activityId) {
        this.$message.error(this.defaultCopy.drawFailed);
        return;
      }

      this.isDrawing = true;
      try {
        const res = await this.$pointsCache.requestWithPointToken((token) => {
          this.pointToken = token;
          return this.$global.pointsLotteryDraw(
            {
              activityId,
              requestId: this.createRequestId(),
            },
            token
          );
        });

        if (!res) {
          this.openLoginWithSource();
          return;
        }

        if (isApiSuccess(res)) {
          const prize = this.extractDrawPrize(res.data);
          this.$refs.lotteryHero?.spinToPrize(prize);
          setTimeout(() => {
            this.showDrawResult(prize);
            this.refreshAfterDraw();
          }, 1600);
          return;
        }

        this.$message.error(res.msg || this.defaultCopy.drawFailed);
      } catch (e) {
        if (!this.$pointsCache.isPointUnauthorized(e)) {
          this.$message.error(this.defaultCopy.drawFailed);
        }
      } finally {
        setTimeout(() => {
          this.isDrawing = false;
        }, 1700);
      }
    },
    // 展示抽奖结果弹窗。
    showDrawResult(prize) {
      this.drawResult = prize || {};
      this.resultDialogVisible = true;
    },
    // 关闭结果弹窗，必要时补刷我的奖品列表。
    closeResultDialog() {
      this.resultDialogVisible = false;
      if (this.needsMyPrizeRefresh) {
        this.needsMyPrizeRefresh = false;
        this.fetchMyPrizeList(1, true);
      }
    },
    // 结果弹窗主按钮根据奖品类型决定关闭或打开地址弹窗。
    handleResultPrimaryAction() {
      if (this.isPhysicalPrizeResult) {
        this.openAddressBook();
        return;
      }

      this.closeResultDialog();
    },
    // 打开地址弹窗并关闭结果弹窗，防止两个弹层叠加。
    openAddressBook() {
      this.addressBookVisible = true;
      this.closeResultDialog();
    },
    // 关闭地址弹窗。
    closeAddressBook() {
      this.addressBookVisible = false;
    },
    // 从我的奖品列表进入地址填写时，先把奖品记录转换成地址弹窗需要的结果对象。
    claimAddressForPrize(item) {
      this.drawResult = {
        ...(item || {}),
        recordId: item?.recordId ?? item?.record_id ?? item?.id ?? "",
      };
      this.openAddressBook();
    },
    // 地址提交成功后刷新我的奖品，展示最新收货信息。
    handleAddressClaimed() {
      this.fetchMyPrizeList(1, true);
    },
    // 把抽奖接口返回归一化成转盘和结果弹窗都能识别的奖品对象。
    extractDrawPrize(data) {
      const detail = data?.record || data?.prize || data || {};
      const drawRecordId =
        data?.recordId ??
        data?.record_id ??
        detail.recordId ??
        detail.record_id ??
        null;
      const wheelPrizeId =
        detail.prizeId ??
        detail.prize_id ??
        detail.prize?.id ??
        null;

      let wheelPrize = null;
      if (wheelPrizeId !== null && wheelPrizeId !== undefined && wheelPrizeId !== "") {
        wheelPrize = this.wheelPrizes.find(
          (item) =>
            String(item.id) === String(wheelPrizeId) ||
            String(item.prizeId) === String(wheelPrizeId)
        );
      }

      if (!wheelPrize) {
        const name = detail.name ?? detail.prize?.name ?? detail.prizeName;
        if (name) {
          wheelPrize = this.wheelPrizes.find((item) => item.name === name);
        }
      }

      if (wheelPrize) {
        return {
          ...wheelPrize,
          ...detail,
          id: wheelPrize.id,
          prizeId: wheelPrize.prizeId ?? wheelPrize.id,
          recordId: drawRecordId,
          name: detail.name ?? wheelPrize.name,
          type: detail.type ?? detail.prizeType ?? wheelPrize.type,
          prizeType: detail.prizeType ?? detail.type ?? wheelPrize.type,
        };
      }

      return {
        ...detail,
        recordId: drawRecordId ?? detail.recordId ?? detail.record_id ?? null,
      };
    },
    // 创建抽奖请求幂等 id，浏览器不支持 randomUUID 时使用本地随机实现。
    createRequestId() {
      if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
      }

      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
        const random = Math.random() * 16 | 0;
        const value = char === "x" ? random : (random & 0x3 | 0x8);
        return value.toString(16);
      });
    },
    // 从空奖品状态滚回顶部抽奖区域。
    scrollToDraw() {
      if (this.$refs.lotteryHero) {
        this.$refs.lotteryHero.$el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    // 从顶部规则入口滚动到活动规则组件。
    scrollToRules() {
      if (this.$refs.lotteryRules) {
        this.$refs.lotteryRules.$el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    // 结果弹窗统一读取奖品名称。
    getPrizeName(prize) {
      return getPrizeName(prize);
    },
    // 结果弹窗统一处理奖品图片缩略参数。
    appendImageResize(url) {
      return appendImageResize(url);
    },
    // 积分值格式化为英文千分位。
    formatPoints(value) {
      const number = this.parsePointValue(value);
      return Number.isFinite(number) ? number.toLocaleString("en-US") : value;
    },
    // 解析接口可能返回的字符串或带逗号积分值。
    parsePointValue(value) {
      const number = Number(String(value || 0).replace(/,/g, ""));
      return Number.isFinite(number) ? number : 0;
    },

  },
};
